import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Edit, Trash2, Search, Package, DollarSign, BarChart3, TrendingUp,
  Loader2, FileText, Receipt, Layers, Truck, X, Calendar as CalendarIcon,
  ToggleLeft, ToggleRight, Filter
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend
} from "recharts";
import api from "@/lib/api";
import { usePermission } from "@/hooks/usePermission";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { KpiCard } from "@/components/KpiCard";

const CHART_COLORS = [
  "hsl(173 80% 40%)",
  "hsl(199 88% 48%)",
  "hsl(38 92% 55%)",
  "hsl(142 70% 42%)",
  "hsl(0 78% 52%)",
  "hsl(262 60% 55%)",
  "hsl(340 70% 52%)",
];

const UNIDADES = ["pieza", "kg", "litro", "metro", "caja", "rollo", "paquete", "otro"];

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

// ─── CATALOGO DE INSUMOS TAB ───────────────────────────────────
function InsumosCatalogTab() {
  const qc = useQueryClient();
  const canManage = usePermission("insumos_manage");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", precio_unitario: "", categoria: "", unidad_medida: "pieza", proveedor: "" });
  const [saving, setSaving] = useState(false);

  const { data: insumos = [], isLoading } = useQuery({
    queryKey: ["insumos"],
    queryFn: async () => (await api.get("/insumos")).data,
  });

  const filtered = useMemo(() => {
    if (!search) return insumos;
    const q = search.toLowerCase();
    return insumos.filter((i) =>
      i.nombre?.toLowerCase().includes(q) ||
      i.categoria?.toLowerCase().includes(q) ||
      i.proveedor?.toLowerCase().includes(q)
    );
  }, [insumos, search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ nombre: "", precio_unitario: "", categoria: "", unidad_medida: "pieza", proveedor: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nombre: item.nombre || "",
      precio_unitario: String(item.precio_unitario ?? ""),
      categoria: item.categoria || "",
      unidad_medida: item.unidad_medida || "pieza",
      proveedor: item.proveedor || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es requerido");
    if (!form.precio_unitario || isNaN(Number(form.precio_unitario))) return toast.error("Precio unitario inválido");
    setSaving(true);
    try {
      const payload = { ...form, precio_unitario: Number(form.precio_unitario) };
      if (editing) {
        await api.patch(`/insumos/${editing.id}`, payload);
        toast.success("Insumo actualizado");
      } else {
        await api.post("/insumos", payload);
        toast.success("Insumo creado");
      }
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ["insumos"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/insumos/${id}`);
      toast.success("Insumo eliminado");
      qc.invalidateQueries({ queryKey: ["insumos"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const toggleActivo = async (item) => {
    try {
      await api.patch(`/insumos/${item.id}`, { activo: !item.activo });
      toast.success(item.activo ? "Insumo desactivado" : "Insumo activado");
      qc.invalidateQueries({ queryKey: ["insumos"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  if (isLoading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar insumo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="insumos-search-input"
          />
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="insumos-create-button">
            <Plus className="h-4 w-4 mr-1" /> Nuevo Insumo
          </Button>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2.5 px-3">Nombre</th>
                  <th className="py-2.5 px-3">Precio Unit.</th>
                  <th className="py-2.5 px-3 hidden md:table-cell">Categoría</th>
                  <th className="py-2.5 px-3 hidden md:table-cell">Unidad</th>
                  <th className="py-2.5 px-3 hidden lg:table-cell">Proveedor</th>
                  <th className="py-2.5 px-3">Estado</th>
                  {canManage && <th className="py-2.5 px-3 w-28">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`insumo-row-${item.id}`}>
                    <td className="py-2.5 px-3 font-medium">{item.nombre}</td>
                    <td className="py-2.5 px-3 font-mono tabular-nums">{fmtMoney(item.precio_unitario)}</td>
                    <td className="py-2.5 px-3 hidden md:table-cell">
                      {item.categoria ? <Badge variant="outline" className="text-xs">{item.categoria}</Badge> : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell text-muted-foreground">{item.unidad_medida || "pieza"}</td>
                    <td className="py-2.5 px-3 hidden lg:table-cell text-muted-foreground">{item.proveedor || "-"}</td>
                    <td className="py-2.5 px-3">
                      {item.activo !== false ? (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/35 text-xs">Activo</Badge>
                      ) : (
                        <Badge className="bg-muted/40 text-muted-foreground border-border text-xs">Inactivo</Badge>
                      )}
                    </td>
                    {canManage && (
                      <td className="py-2.5 px-3">
                        <div className="flex gap-1">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleActivo(item)} data-testid={`insumo-toggle-${item.id}`}>
                                  {item.activo !== false ? <ToggleRight className="h-3.5 w-3.5 text-emerald-400" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{item.activo !== false ? "Desactivar" : "Activar"}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`insumo-edit-${item.id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`insumo-delete-${item.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar "{item.nombre}"?</AlertDialogTitle>
                                <AlertDialogDescription>Si el insumo tiene costos registrados, no se puede eliminar. Desactívalo en su lugar.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(item.id)} data-testid="insumo-delete-confirm">Eliminar</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 7 : 6} className="py-12 text-center text-muted-foreground">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      {search ? "Sin resultados" : "Sin insumos. Agrega el primero."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Insumo" : "Nuevo Insumo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="insumo-nombre-input" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Precio Unitario *</Label>
                <Input type="number" step="0.01" min="0" value={form.precio_unitario} onChange={(e) => setForm({ ...form, precio_unitario: e.target.value })} data-testid="insumo-precio-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Unidad de Medida</Label>
                <Select value={form.unidad_medida} onValueChange={(v) => setForm({ ...form, unidad_medida: v })}>
                  <SelectTrigger data-testid="insumo-unidad-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNIDADES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Categoría</Label>
              <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="Ej: Empaque, Etiquetas, Herramientas..." data-testid="insumo-categoria-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Proveedor</Label>
              <Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} data-testid="insumo-proveedor-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="insumo-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── REGISTRO DE COSTOS TAB ───────────────────────────────────────
function RegistroCostosTab() {
  const qc = useQueryClient();
  const canManage = usePermission("costos_manage");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterFolio, setFilterFolio] = useState("");
  const [form, setForm] = useState({ insumo_id: "", cantidad: "", ot_id: "", fecha: new Date().toISOString().slice(0, 10), notas: "" });

  const { data: insumos = [] } = useQuery({
    queryKey: ["insumos"],
    queryFn: async () => (await api.get("/insumos", { params: { activo: true } })).data,
  });

  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (filterFolio) params.ot_folio = filterFolio;

  const { data: costosData, isLoading } = useQuery({
    queryKey: ["costos-maquila", params],
    queryFn: async () => (await api.get("/costos-maquila", { params })).data,
  });

  const costos = costosData?.items || [];
  const totalCostos = costosData?.total || 0;

  const selectedInsumo = insumos.find((i) => i.id === form.insumo_id);
  const calculatedTotal = selectedInsumo && form.cantidad ? (Number(form.cantidad) * selectedInsumo.precio_unitario) : 0;

  const openCreate = () => {
    setForm({ insumo_id: "", cantidad: "", ot_id: "", fecha: new Date().toISOString().slice(0, 10), notas: "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.insumo_id) return toast.error("Selecciona un insumo");
    if (!form.cantidad || Number(form.cantidad) <= 0) return toast.error("Cantidad debe ser mayor a 0");
    setSaving(true);
    try {
      await api.post("/costos-maquila", {
        insumo_id: form.insumo_id,
        cantidad: Number(form.cantidad),
        ot_id: form.ot_id || "",
        fecha: form.fecha,
        notas: form.notas,
      });
      toast.success("Costo registrado");
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ["costos-maquila"] });
      qc.invalidateQueries({ queryKey: ["costos-resumen"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const removeCosto = async (id) => {
    try {
      await api.delete(`/costos-maquila/${id}`);
      toast.success("Registro eliminado");
      qc.invalidateQueries({ queryKey: ["costos-maquila"] });
      qc.invalidateQueries({ queryKey: ["costos-resumen"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const sumTotal = costos.reduce((s, c) => s + (c.total || 0), 0);

  return (
    <div className="space-y-4">
      {/* Filters + Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="costos-date-from" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="costos-date-to" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Folio OT</Label>
          <Input placeholder="Ej: KL01" value={filterFolio} onChange={(e) => setFilterFolio(e.target.value)} className="w-32" data-testid="costos-filter-folio" />
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm font-mono tabular-nums py-1 px-3" data-testid="costos-sum-badge">
            Total: {fmtMoney(sumTotal)}
          </Badge>
          {canManage && (
            <Button onClick={openCreate} data-testid="costos-create-button">
              <Plus className="h-4 w-4 mr-1" /> Registrar Costo
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2.5 px-3">Fecha</th>
                  <th className="py-2.5 px-3">Insumo</th>
                  <th className="py-2.5 px-3">Cant.</th>
                  <th className="py-2.5 px-3">P. Unit.</th>
                  <th className="py-2.5 px-3">Total</th>
                  <th className="py-2.5 px-3">OT</th>
                  <th className="py-2.5 px-3">Notas</th>
                  {canManage && <th className="py-2.5 px-3 w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {isLoading && [1,2,3].map(i => (
                  <tr key={i} className="border-t border-border/60">
                    {Array(canManage ? 8 : 7).fill(0).map((_, j) => <td key={j} className="py-2.5 px-3"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))}
                {!isLoading && costos.map((c) => (
                  <tr key={c.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`costo-row-${c.id}`}>
                    <td className="py-2.5 px-3 font-mono tabular-nums text-xs">{c.fecha}</td>
                    <td className="py-2.5 px-3 font-medium">{c.insumo_nombre}</td>
                    <td className="py-2.5 px-3 font-mono tabular-nums">{c.cantidad} <span className="text-muted-foreground text-xs">{c.unidad_medida}</span></td>
                    <td className="py-2.5 px-3 font-mono tabular-nums">{fmtMoney(c.precio_unitario)}</td>
                    <td className="py-2.5 px-3 font-mono tabular-nums font-semibold text-primary">{fmtMoney(c.total)}</td>
                    <td className="py-2.5 px-3">
                      {c.ot_folio ? <Badge variant="outline" className="font-mono text-xs">{c.ot_folio}</Badge> : <span className="text-muted-foreground text-xs">General</span>}
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground text-xs max-w-[150px] truncate">{c.notas || "-"}</td>
                    {canManage && (
                      <td className="py-2.5 px-3">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" data-testid={`costo-delete-${c.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminar registro?</AlertDialogTitle>
                              <AlertDialogDescription>Se eliminará el costo de {fmtMoney(c.total)} ({c.insumo_nombre}).</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeCosto(c.id)} data-testid="costo-delete-confirm">Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    )}
                  </tr>
                ))}
                {!isLoading && costos.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 8 : 7} className="py-12 text-center text-muted-foreground">
                      <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      Sin registros de costos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Cost Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Costo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Insumo *</Label>
              <Select value={form.insumo_id} onValueChange={(v) => setForm({ ...form, insumo_id: v })}>
                <SelectTrigger data-testid="costo-insumo-select"><SelectValue placeholder="Seleccionar insumo..." /></SelectTrigger>
                <SelectContent>
                  {insumos.map((ins) => (
                    <SelectItem key={ins.id} value={ins.id}>
                      {ins.nombre} — {fmtMoney(ins.precio_unitario)} / {ins.unidad_medida}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cantidad *</Label>
                <Input type="number" step="0.01" min="0.01" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} data-testid="costo-cantidad-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Total Calculado</Label>
                <div className="h-10 flex items-center px-3 rounded-md border border-border bg-muted/30 font-mono tabular-nums text-primary font-semibold" data-testid="costo-total-preview">
                  {fmtMoney(calculatedTotal)}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Folio OT (opcional)</Label>
              <Input value={form.ot_id} onChange={(e) => setForm({ ...form, ot_id: e.target.value })} placeholder="Ej: KL01 (dejar vacío = gasto general)" data-testid="costo-ot-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Fecha</Label>
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="costo-fecha-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notas</Label>
              <Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Observaciones..." data-testid="costo-notas-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="costo-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── RESUMEN TAB ──────────────────────────────────────────────────
function ResumenTab() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const { data: resumen, isLoading } = useQuery({
    queryKey: ["costos-resumen", params],
    queryFn: async () => (await api.get("/costos-maquila/resumen", { params })).data,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {[1,2].map(i => <Skeleton key={i} className="h-64 w-full" />)}
        </div>
      </div>
    );
  }

  const r = resumen || {};

  return (
    <div className="space-y-4">
      {/* Date filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="resumen-date-from" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="resumen-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }}>
            <X className="h-3 w-3 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard icon={DollarSign} label="Total General" value={fmtMoney(r.total_general)} delta={`${r.total_registros} registros`} testId="kpi-costos-total" />
        <KpiCard icon={FileText} label="Costos con OT" value={fmtMoney(r.total_con_ot)} delta={`${r.registros_con_ot} registros`} testId="kpi-costos-con-ot" />
        <KpiCard icon={Layers} label="Gastos Generales" value={fmtMoney(r.total_sin_ot)} delta={`${r.registros_sin_ot} registros`} testId="kpi-costos-sin-ot" />
        <KpiCard icon={TrendingUp} label="Promedio por Registro" value={fmtMoney(r.total_registros ? r.total_general / r.total_registros : 0)} delta="por movimiento" testId="kpi-costos-promedio" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/* Costo por mes */}
        <Card className="bg-card border-border" data-testid="chart-costos-por-mes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Costo por Mes</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">Tendencia de gastos mensual</CardDescription>
          </CardHeader>
          <CardContent>
            {(r.por_mes || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={r.por_mes}>
                  <CartesianGrid vertical={false} stroke="hsl(215 16% 18%)" strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fill: "hsl(215 12% 70%)", fontSize: 11 }} />
                  <YAxis tick={{ fill: "hsl(215 12% 70%)", fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <ReTooltip contentStyle={{ background: "hsl(215 24% 11%)", border: "1px solid hsl(215 16% 18%)", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
                  <Area type="monotone" dataKey="total" stroke="hsl(173 80% 40%)" fill="hsl(173 80% 40%)" fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </CardContent>
        </Card>

        {/* Top insumos */}
        <Card className="bg-card border-border" data-testid="chart-top-insumos">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Top Insumos</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">Insumos con mayor gasto</CardDescription>
          </CardHeader>
          <CardContent>
            {(r.top_insumos || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={r.top_insumos} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid horizontal={false} stroke="hsl(215 16% 18%)" strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fill: "hsl(215 12% 70%)", fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="nombre" width={100} tick={{ fill: "hsl(215 12% 70%)", fontSize: 10 }} />
                  <ReTooltip contentStyle={{ background: "hsl(215 24% 11%)", border: "1px solid hsl(215 16% 18%)", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="total" fill="hsl(199 88% 48%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </CardContent>
        </Card>

        {/* Por categoría (Pie) */}
        <Card className="bg-card border-border" data-testid="chart-costos-por-categoria">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Por Categoría</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">Distribución de gasto por categoría</CardDescription>
          </CardHeader>
          <CardContent>
            {(r.por_categoria || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={r.por_categoria} dataKey="total" nameKey="nombre" cx="50%" cy="50%" outerRadius={80} label={({ nombre, percent }) => `${nombre} ${(percent * 100).toFixed(0)}%`}>
                    {(r.por_categoria || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <ReTooltip contentStyle={{ background: "hsl(215 24% 11%)", border: "1px solid hsl(215 16% 18%)", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </CardContent>
        </Card>

        {/* Top OTs */}
        <Card className="bg-card border-border" data-testid="chart-top-ots-costos">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Top OTs por Costo</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">OTs con mayor gasto asociado</CardDescription>
          </CardHeader>
          <CardContent>
            {(r.top_ots || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={r.top_ots} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid horizontal={false} stroke="hsl(215 16% 18%)" strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fill: "hsl(215 12% 70%)", fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="folio" width={80} tick={{ fill: "hsl(215 12% 70%)", fontSize: 11 }} />
                  <ReTooltip contentStyle={{ background: "hsl(215 24% 11%)", border: "1px solid hsl(215 16% 18%)", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="total" fill="hsl(38 92% 55%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">Sin datos de OTs</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────
export default function CostosMaquilaPage() {
  const canManageInsumos = usePermission("insumos_manage");
  const canManageCostos = usePermission("costos_manage");

  return (
    <div className="space-y-4" data-testid="costos-maquila-page">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Costos de Maquila</h1>
        <p className="text-sm text-muted-foreground">Gestiona insumos, registra gastos y revisa el resumen de costos.</p>
      </div>
      <Tabs defaultValue="registro">
        <TabsList>
          <TabsTrigger value="registro" data-testid="costos-tab-registro">
            <Receipt className="h-4 w-4 mr-1.5" /> Registro
          </TabsTrigger>
          <TabsTrigger value="catalogo" data-testid="costos-tab-catalogo">
            <Package className="h-4 w-4 mr-1.5" /> Catálogo Insumos
          </TabsTrigger>
          <TabsTrigger value="resumen" data-testid="costos-tab-resumen">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Resumen
          </TabsTrigger>
        </TabsList>
        <TabsContent value="registro" className="mt-3">
          <RegistroCostosTab />
        </TabsContent>
        <TabsContent value="catalogo" className="mt-3">
          <InsumosCatalogTab />
        </TabsContent>
        <TabsContent value="resumen" className="mt-3">
          <ResumenTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
